import React, { useCallback, useState } from 'react';

import { DesignElement, DesignElementKind, DesignCanvasLayer } from 'models/cms/booths';
import { useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import getVideoUrlByLocale from 'models/helpers/getVideoUrlByLocale';
import SliderImage from './SliderImage';
import ImageLightbox from './SliderImageLightbox';
import SliderVideo from './SliderVideo';
import SliderRichText from './SliderRichText';

const MobileBoothBanner = ({
  asset,
  onPauseVideo,
  onPlayVideo,
  onShowPopup,
  onHidePopup,
  active,
  boothVideoAutoplay = true,
}: {
  asset: DesignElement | DesignCanvasLayer;
  onPauseVideo: () => void;
  onPlayVideo: () => void;
  onShowPopup: () => void;
  onHidePopup: () => void;
  active: boolean;
  boothVideoAutoplay: boolean;
}) => {
  const locale = useSelector(selectLocale);
  const [imageLightboxOpen, setImageLightboxOpen] = useState(false);

  const openImageOverlay = useCallback(() => {
    setImageLightboxOpen(true);
    if (onShowPopup) {
      onShowPopup();
    }
  }, [onShowPopup]);

  const closeImageOverlay = useCallback(() => {
    setImageLightboxOpen(false);
    if (onHidePopup) {
      onHidePopup();
    }
  }, [onHidePopup]);

  const { link, actionUrl } = asset as any;
  const resolvedLink = link || actionUrl;

  switch (asset.kind) {
    case DesignElementKind.Video: {
      const { video, thumbnailImage } = asset;
      const videoUrl = getVideoUrlByLocale(video, locale);
      const thumbnailUrl = findAvailableLocale(thumbnailImage, locale)?.originalImageUrl;
      if (!videoUrl) return null;
      return (
        <SliderVideo
          thumbnail={thumbnailUrl}
          url={videoUrl}
          onPauseVideo={onPauseVideo}
          onPlayVideo={onPlayVideo}
          active={active}
          autoplay={boothVideoAutoplay}
        />
      );
    }
    case DesignElementKind.Image:
      return (
        resolvedLink
          ? (
            <SliderImage
              img={asset.image[locale].originalImageUrl}
              onClick={() => window.open(`http://${resolvedLink.replace(/\b(https:\/\/|http:\/\/)/, '')}`, '_blank')}
            />
          )
          : (
            <>
              <SliderImage
                img={asset.image[locale].originalImageUrl}
                onClick={openImageOverlay}
              />
              <ImageLightbox
                src={asset.image[locale].originalImageUrl}
                open={imageLightboxOpen}
                onClose={closeImageOverlay}
              />
            </>
          )
      );
    case DesignElementKind.PDF: {
      const pdfUrl = asset.pdf[locale]?.fileUrl;
      return (
        <SliderImage
          img={asset.thumbnailImage[locale]?.originalImageUrl}
          onClick={() => pdfUrl && window.open(pdfUrl, '_blank')}
        />
      );
    }
    case DesignElementKind.Richtext:
    default:
      return (
        <SliderRichText
          text={asset.contentRichtextHtml[locale] || ''}
        />
      );
  }
};

export default MobileBoothBanner;
